import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import type { UntypedFormArray } from '@angular/forms';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import deepmerge from 'deepmerge';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, EMPTY, forkJoin, from, merge, Observable, Subscription } from 'rxjs';
import { catchError, concatMap, debounceTime, delay, filter, map, share, startWith, take, tap } from 'rxjs/operators';
import { FloorClass } from '../../../../models/floor.class';
import { SHOW_FLOOR_INDEX_FEATURE_KEY } from '../../../../shared/constants/features.const';
import { isNotNullOrEmptyString } from '../../../../shared/utils/functions';
import { Toolbar } from '../../../actions-toolbar/actions-toolbar.model';
import { ActionsToolbarService } from '../../../actions-toolbar/actions-toolbar.service';
import { EDPFloorSpaceInfo, UpdateStackingPlanInfo } from '../../../floor-container/floor/floor.model';
import { OverlayService } from '../../../floor-container/floor/item/overlay.service';
import { FloorOption } from '../elevators/elevators.model';
import {
  AboveBelow,
  ConflictSpaces,
  FLOOR_HEADERS,
  FloorFGModel,
  FloorPlanModel,
  IncreaseDecreaseFloors,
  SelectedFloor,
  ValueUOMFG,
} from './floors.models';
import { FloorsValidators } from './floors.validators';
import { AbstractControlWithWarnings } from './warn';
import { MoveFloorComponent } from './move-floor/move-floor.component';
import { DeleteFloorSelectedComponent } from './delete-floor-selected/delete-floor-selected.component';
import { CbreAlertService } from '@cbre/alerts';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';
import { UOM_VALUES } from '@miq/utils';
import { LovValues } from '../../../../stacking-plan.model';
import { v4 as uuidV4 } from 'uuid';

/** Header fields sent by `updateProperty`; if none changed the write is skipped */
const HEADER_FIELDS: (keyof Toolbar)[] = [
  'netRentableArea',
  'netRentableAreaUOM',
  'totalNumberOfFloors',
  'underGroundFloors',
  'commonFloorSize',
  'commonFloorSizeUOM',
];

@UntilDestroy()
@Component({
  selector: 'sp-floors',
  templateUrl: './floors.component.html',
  styleUrls: ['./floors.component.scss'],
  standalone: false,
})
export class FloorsComponent implements OnInit, OnDestroy {
  @Input() floorData: FloorClass[];
  @Input() floorsHeaderData: Toolbar;
  @Input() elevatorSelectedFloors: number[];
  @Input() localeId: number;
  @Output() updateFloorData: EventEmitter<UpdateStackingPlanInfo> = new EventEmitter();

  get floorFormArray() {
    return this.floorsFG.controls.floor as UntypedFormArray;
  }
  get generalFormGroup() {
    return this.floorsFG.controls.general as UntypedFormGroup;
  }

  get generalNraFG() {
    return this.generalFormGroup.controls.propertyNRA as UntypedFormGroup;
  }

  get generalCommonFloorSizeFG() {
    return this.generalFormGroup.controls.propertyCommonFloorSize as UntypedFormGroup;
  }

  asWarn(control: AbstractControl | null | undefined): AbstractControlWithWarnings {
    return control as AbstractControlWithWarnings;
  }

  nonRentableSpace = 'NonRentableSpace';
  uomValues: LovValues[] = [];
  distanceUOMValues: LovValues[] = [];
  conflictSpaces: ConflictSpaces[];
  floorHeaders = FLOOR_HEADERS.map(f => ({ ...f }));
  floorsFG: UntypedFormGroup;
  floors: FloorClass[] = [];
  floorsHeader: Toolbar;
  totalActualIndexedFloors: number;
  totalUnderGroundFloors: number;
  floorsOptions: FloorOption[] = [];
  /** @deprecated temporary usage */
  showFloorIndexFeatureKey = SHOW_FLOOR_INDEX_FEATURE_KEY;
  selectedFloors: SelectedFloor[] = [];
  areAllFloorsChecked = false;
  uploadingFloorPlan = false;
  minFloorArea: number | undefined;
  minFloorAreaUOM: string | undefined;
  maxFloorArea: number | undefined;
  maxFloorAreaUOM: string | undefined;
  moveFloorStatus = false;
  deleteFloorsTaskManager = new BehaviorSubject(false);
  confirmFloorsDelete = false;

  #loadindData = new BehaviorSubject(true);
  loadindData$ = this.#loadindData.asObservable();
  #uomsLoadState = new BehaviorSubject(false);
  uomsLoaded$ = this.#uomsLoadState.asObservable().pipe(filter(Boolean));

  private floorsFGSubscription: Subscription;

  constructor(
    private fb: UntypedFormBuilder,
    private actionsToolbarService: ActionsToolbarService,
    @Inject(FEATURE_TOGGLER) private toggleRouterService: FeatureToggler,
    private overlayService: OverlayService,
    private cbreAlertService: CbreAlertService
  ) {
    if (toggleRouterService.isFeatureEnabled(this.showFloorIndexFeatureKey)) {
      this.floorHeaders.splice(1, 0, {
        field: 'floorIndex',
        header: 'moreInfo.floorIndex',
        headerClass: 'floor-index-header',
        colClass: 'floor-name-col',
      });
    }
  }

  ngOnInit() {
    this.getUomLOVs()
      .pipe(
        untilDestroyed(this),
        // ponytail: one frame is enough for the dialog to paint first; use MatDialogRef.afterOpened() if it ever flickers
        delay(100),
        tap(() => this.#loadindData.next(false)),
        catchError(() => {
          this.#loadindData.next(false);
          return EMPTY;
        })
      )
      .subscribe();
    this.floors = FloorClass.clone(this.floorData);
    this.floorsHeader = deepmerge([], this.floorsHeaderData);

    // above ground floors
    const aboveGroundFloors = FloorClass.getAboveGround(this.floors);
    this.totalActualIndexedFloors = FloorClass.getMaxIndex(aboveGroundFloors) + 1;

    // parkingFloors
    const parkingFloors = FloorClass.getUnderground(this.floors);
    this.totalUnderGroundFloors = parkingFloors.length;
    this.formInit(aboveGroundFloors, parkingFloors);
  }

  ngOnDestroy(): void {
    /* required by Angular-l10n */
  }

  /**
   * When user clicks on save button in floor component, the floor number
   * changes, nra changes will update in floorData array and toolbarData object.
   * From here we are sending the floor data in actual json format and toolbar
   * data to parent component(more info dialog) through output event emitter
   */
  public saveFloorInfo() {
    this.updateFloorValuesFromFormArray(this.floors, this.floorFormArray);
    const generalFGValue = this.generalFormGroup.value;
    const { propertyNRA, propertyCommonFloorSize, numberOfFloors, underGroundFloors } = generalFGValue;
    const hasCommonSizeVal = !!propertyCommonFloorSize.val;
    const hasNRAVal = !!propertyNRA.val;

    const toolbarData: Toolbar = {
      netRentableArea: hasNRAVal ? propertyNRA.val : null,
      netRentableAreaUOM: hasNRAVal ? propertyNRA.uom : null,
      totalNumberOfFloors: numberOfFloors,
      underGroundFloors,
      commonFloorSize: hasCommonSizeVal ? propertyCommonFloorSize.val : null,
      commonFloorSizeUOM: hasCommonSizeVal ? propertyCommonFloorSize.uom : null,
    };

    /*
     * Construct the floor data as like the original json format, since
     * stacking plan main component required floor info in below format. From
     * stacking plan main component, it will update the floor info in child
     * components
     */
    const updatedFloorData: EDPFloorSpaceInfo[] = this.floors.map(floor => floor.toEDPFloorSpaceInfo());
    // compare values, not `dirty`: add/remove-floor actions call setValue without dirtying
    const headerChanged = HEADER_FIELDS.some(
      key => String(toolbarData[key] ?? '') !== String(this.floorsHeader[key] ?? '')
    );
    const updateStackingPlanData: UpdateStackingPlanInfo = {
      updatedFloorData,
      toolbarData,
      headerChanged,
    };
    this.updateFloorData.emit(updateStackingPlanData);
  }

  public preventSignKeys(event: KeyboardEvent): void {
    if (event.key === '+' || event.key === '-') {
      event.preventDefault();
    }
  }

  /**
   * It will add new indexed floors at last in the floors array or
   * deletes indexed floors from the last in the floors array based on the
   * floors input field value and total indexed floors
   */
  public updateFloorsPositions() {
    const numberOfFloorsFC = this.generalFormGroup.get('numberOfFloors');
    const numberOfUndergroundFloorsFC = this.generalFormGroup.get('underGroundFloors');
    if (
      !numberOfFloorsFC.errors &&
      !numberOfUndergroundFloorsFC.errors &&
      numberOfFloorsFC.value != null &&
      numberOfUndergroundFloorsFC.value != null
    ) {
      const oldFloorsWithUpdatedValues = this.cloneAndUpdateFloors();
      const aboveGroundFloors = FloorClass.getAboveGround(oldFloorsWithUpdatedValues);
      const totalNumberOfFloors = this.getTotalNumberOfFloors(aboveGroundFloors, numberOfFloorsFC.value);
      this.generateUpdatedFloors(oldFloorsWithUpdatedValues, numberOfUndergroundFloorsFC.value, totalNumberOfFloors);
    }
  }

  private getTotalNumberOfFloors(aboveGroundFloors: FloorClass[], numberOfFloors: number) {
    const numberOfHiddenFloors = aboveGroundFloors.filter(item => item.hideFloor).length;
    return numberOfHiddenFloors + numberOfFloors;
  }

  /**
   * It is used to upload an image to backend
   * and returns file path
   */
  public onFileChange(result: { event: any; floors: SelectedFloor[] }) {
    const { event, floors } = result;
    const formData: FormData = new FormData();
    formData.append('file', event.target.files[0], event.target.files[0].name);
    this.uploadingFloorPlan = true;
    this.actionsToolbarService.uploadFloorPlan(formData).subscribe((fileData: FloorPlanModel) => {
      this.uploadingFloorPlan = false;
      floors.forEach(({ floorGroup }) => {
        floorGroup.controls.floorPlan.patchValue({
          link: fileData.link,
          name: fileData.name,
        });
        floorGroup.markAsDirty();
        floorGroup.markAsTouched();
      });
    });
  }

  public selectAllFloors(event) {
    this.areAllFloorsChecked = event.checked;
    if (this.areAllFloorsChecked) {
      this.selectedFloors = this.floors.map((_, floorIndex) => ({
        floorGroup: this.floorFormArray.controls[floorIndex] as UntypedFormGroup,
        floorIndex,
      }));
    } else {
      this.selectedFloors = [];
    }
  }

  public floorChecked() {
    this.areAllFloorsChecked = this.floors.length === this.selectedFloors.length;
  }

  public updateFloorSize() {
    const floorSizeGroup: ValueUOMFG = this.generalCommonFloorSizeFG.value as ValueUOMFG;
    this.changeFloorsSizesFGValues('val', floorSizeGroup.val);
    this.changeFloorsSizesFGValues('uom', floorSizeGroup.uom);
  }

  public toggleHiddenFloor(floors: SelectedFloor[]) {
    floors.forEach(({ floorGroup }) => {
      floorGroup.markAsDirty();
      floorGroup.markAsTouched();

      const hideFloorFC = floorGroup.controls.hideFloor;
      const floorIndexFC = floorGroup.controls.floorIndex;

      // FloorClass.getMaxRealIndex & FloorClass.getMinRealIndex requires updated floor info to set validations
      this.floors.find(floorInstance => floorInstance.floorIndex === floorIndexFC.value).hideFloor = hideFloorFC.value;
      if (FloorClass.isFloorIndexValid(floorIndexFC.value)) {
        const setNoOfFloors = hideFloorFC.value ? IncreaseDecreaseFloors.Increase : IncreaseDecreaseFloors.Decrease;
        this.resetValidations(floorIndexFC.value, setNoOfFloors);
      }
    });
  }

  public deleteFloorOverlay(origin: HTMLElement): void {
    this.overlayService
      .openOverlayBox<DeleteFloorSelectedComponent>(
        DeleteFloorSelectedComponent,
        {
          floors: this.floors,
          elevators: this.elevatorSelectedFloors,
          floorsAboveGround: this.generalFormGroup.get('numberOfFloors').value,
          floorsBelowGround: this.generalFormGroup.get('underGroundFloors').value,
        },
        origin,
        '',
        null,
        'moveFloor'
      )
      .subscribe(({ floorNumber, isAboveGround, conflictSpaces, callApi }) => {
        if (callApi) {
          this.deleteFloor({ floorNumber, isAboveGround });
        } else {
          this.conflictSpaces = conflictSpaces;
        }
      });
  }

  private deleteFloor(floorDetails): void {
    this.confirmFloorsDelete = true;
    this.deleteFloorsTaskManager.pipe(take(2)).subscribe(deleteFloor => {
      if (!deleteFloor) {
        return;
      }
      const { floorNumber, isAboveGround } = floorDetails;
      const floorsInfo = FloorClass.clone(this.floorData).filter(item =>
        isAboveGround ? item.floorIndex > Number(floorNumber) : item.floorIndex < Number(floorNumber)
      );
      const floorsWithId = floorsInfo.filter(floorInfo => floorInfo?.floorId);
      if (floorsWithId.length === 0) {
        this.updateFloorsForDeleteButton();
        this.confirmFloorsDelete = false;
        this.deleteFloorsTaskManager.next(false);
        return;
      }
      this.processFloorsSequentially(floorsWithId);
    });
  }

  /**
   * Best solution: Pure RxJS sequential processing with robust error handling
   * Guarantees sequential execution and continues on errors without setTimeout
   */
  private processFloorsSequentially(floorsWithId: FloorClass[]): void {
    from(floorsWithId)
      .pipe(
        concatMap(floorInfo => {
          return this.actionsToolbarService.saveStackingPlanUpdate([floorInfo]).pipe(
            catchError(() => {
              this.cbreAlertService.showErrorSnackBar_l10n('errorMessages.deleteFloor');
              return EMPTY;
            })
          );
        }),
        untilDestroyed(this)
      )
      .subscribe({
        complete: () => {
          this.updateFloorsForDeleteButton();
          this.confirmFloorsDelete = false;
          this.deleteFloorsTaskManager.next(false);
        },
      });
  }
  private updateFloorsForDeleteButton(): void {
    this.updateFloorsPositions();
    this.floorFormArray.markAsDirty();
    this.floorFormArray.markAsTouched();
  }

  private resetValidations(floorIndex: number, setNoOfFloors?: string) {
    // above ground floors
    const maxExistingFloorIndex = FloorClass.getMaxRealIndex(this.floors, false) + 1;
    const aboveGroundFloors = FloorClass.getAboveGround(this.floors);
    const numberOfHiddenFloors = aboveGroundFloors.filter(
      item => item.hideFloor && item.floorIndex < maxExistingFloorIndex
    ).length;
    // parkingFloors
    const minExistingFloorIndex = FloorClass.getMinRealIndex(FloorClass.getUnderground(this.floors), false);
    const parkingFloors = FloorClass.getUnderground(this.floors);
    const numberOfHiddenParkingFloors = parkingFloors.filter(
      item => item.hideFloor && item.floorIndex > minExistingFloorIndex
    ).length;
    if (floorIndex >= 0) {
      this.updateUGandAGFloorsCount('numberOfFloors', maxExistingFloorIndex - numberOfHiddenFloors, setNoOfFloors);
    }
    if (floorIndex < 0) {
      const minValidationVal = Math.abs(minExistingFloorIndex) - numberOfHiddenParkingFloors;
      this.updateUGandAGFloorsCount('underGroundFloors', minValidationVal, setNoOfFloors);
    }
  }

  /**
   * It will update(plus or minus) the above ground(AG)/under ground(UG) floors value and will sets the new min validation value
   */
  private updateUGandAGFloorsCount(numberOfFloors: string, minValidation: number, incDesc?: string) {
    const floorsFC: UntypedFormControl = this.generalFormGroup.controls[numberOfFloors] as UntypedFormControl;
    const maxValue =
      numberOfFloors === 'numberOfFloors'
        ? FloorClass.maxNumberOfFloorsAboveGround
        : FloorClass.maxNumberOfFloorsUnderGround;
    floorsFC.setValidators([
      FloorsValidators.asWarning(Validators.min(minValidation), Validators.max(maxValue), Validators.required),
    ]);
    if (incDesc) {
      const setValue = incDesc === IncreaseDecreaseFloors.Increase ? floorsFC.value - 1 : floorsFC.value + 1;
      floorsFC.patchValue(setValue);
      floorsFC.setValidators([
        FloorsValidators.asWarning(Validators.min(minValidation), Validators.max(maxValue), Validators.required),
      ]);
      floorsFC.markAsDirty();
    }
    floorsFC.markAsTouched();
    floorsFC.updateValueAndValidity();
  }

  public showDetailsOverlay(origin: HTMLElement) {
    this.moveFloorStatus = true;
    this.initFloorOptions();
    this.overlayService
      .openOverlayBox<MoveFloorComponent>(MoveFloorComponent, this.floorsOptions, origin, '', null, 'moveFloor')
      .subscribe(moveFloorData => {
        this.moveFloorStatus = false;
        if (!moveFloorData) {
          return;
        }
        const { aboveBelow, floorIndex } = moveFloorData;
        // clone floors and update values with any previously updated value
        const oldFloorsWithUpdatedValues = this.cloneAndUpdateFloors();

        // place -99 indexed floor to appropriate position in the floor data
        // and shifts floor index based on the position for which -99 indexed floor is moved
        const { numberOfUndergroundFloorsFC, numberOfFloorsFC, aboveBelowGround } = this.placeMovedFloor(
          oldFloorsWithUpdatedValues,
          Number(floorIndex),
          aboveBelow
        );

        // generate new array of floors with original values plus new floors
        this.generateUpdatedFloors(oldFloorsWithUpdatedValues, numberOfUndergroundFloorsFC, numberOfFloorsFC);

        this.resetValidations(aboveBelowGround);
        this.floorFormArray.markAsTouched();
        this.floorFormArray.markAsDirty();
      });
  }

  /**
   * clone floors and update values with any previously updated value
   */
  private cloneAndUpdateFloors(): FloorClass[] {
    const oldFloorsWithUpdatedValues = FloorClass.clone(this.floors);
    this.updateFloorValuesFromFormArray(oldFloorsWithUpdatedValues, this.floorFormArray);
    return oldFloorsWithUpdatedValues;
  }

  /**
   * place -99 indexed floor to appropriate position in the floor data
   */
  private placeMovedFloor(oldFloorsWithUpdatedValues: FloorClass[], floorIndex: number, aboveBelow: string) {
    const moveFloorIndex = floorIndex + (aboveBelow === AboveBelow.Above ? 1 : 0);
    const floorToBeMoved = oldFloorsWithUpdatedValues.splice(this.selectedFloors[0].floorIndex, 1)[0];
    const absoluteMoveFloorIndex =
      oldFloorsWithUpdatedValues.findIndex(floor => floor.floorIndex === floorIndex) +
      (aboveBelow === AboveBelow.Above ? 0 : 1);
    oldFloorsWithUpdatedValues.splice(absoluteMoveFloorIndex, 0, floorToBeMoved);
    return this.shiftFloorIndexValues(oldFloorsWithUpdatedValues, moveFloorIndex, aboveBelow, absoluteMoveFloorIndex);
  }

  /**
   * shifts floor index based on the position for which -99 indexed floor is moved
   */
  private shiftFloorIndexValues(
    oldFloorsWithUpdatedValues: FloorClass[],
    moveFloorIndex: number,
    aboveBelow: string,
    absoluteMoveFloorIndex: number
  ) {
    const numberOfFloorsFC = this.generalFormGroup.get('numberOfFloors') as UntypedFormControl;
    const numberOfUndergroundFloorsFC = this.generalFormGroup.get('underGroundFloors') as UntypedFormControl;
    let aboveBelowGround: number;
    if (moveFloorIndex > -1 || (moveFloorIndex === -1 && aboveBelow === AboveBelow.Above)) {
      for (let i = absoluteMoveFloorIndex; i >= 0; i--) {
        oldFloorsWithUpdatedValues[i].floorIndex = moveFloorIndex;
        moveFloorIndex++;
      }
      aboveBelowGround = 0;
    } else {
      for (let i = absoluteMoveFloorIndex; i < oldFloorsWithUpdatedValues.length; i++) {
        moveFloorIndex--;
        oldFloorsWithUpdatedValues[i].floorIndex = moveFloorIndex;
      }
      aboveBelowGround = -1;
    }
    return {
      numberOfFloorsFC: numberOfFloorsFC.value,
      numberOfUndergroundFloorsFC: numberOfUndergroundFloorsFC.value,
      aboveBelowGround,
    };
  }

  /**
   * generate new array of floors with original values plus new floors
   * const currentUndergroundFloors = FloorClass.getUnderground(oldFloorsWithUpdatedValues);
   */
  private generateUpdatedFloors(
    oldFloorsWithUpdatedValues: FloorClass[],
    numberOfUndergroundFloors: number,
    numberOfFloors: number
  ) {
    // get index to start from based on the country
    const startFloorsFrom = FloorClass.getStartingFloorIndex(this.localeId);
    this.floors = [
      ...FloorClass.getInvalidIndexed(oldFloorsWithUpdatedValues),
      ...FloorClass.generateUndergroundFloors(numberOfUndergroundFloors, oldFloorsWithUpdatedValues),
      ...FloorClass.generateAboveGroundFloors(numberOfFloors, oldFloorsWithUpdatedValues, startFloorsFrom),
    ].reverse();

    // generate new floor form array using any previously updated value
    const floorsFA = this.generateFloorsFormArray(this.floors);
    this.floorsFG.setControl('floor', floorsFA);
    this.selectedFloors = [];
  }

  /**
   * Construct the floorsFG form group and its child(General) form group's form
   * controls  and form array(floor)'s form controls
   */
  private formInit(aboveGroundFloors: FloorClass[], parkingFloors: FloorClass[]) {
    const maxExistingFloorIndex = FloorClass.getMaxRealIndex(this.floors, false) + 1;
    const minExistingFloorIndex = FloorClass.getMinRealIndex(FloorClass.getUnderground(this.floors), false);
    const nraInitVal = parseFloat(`${this.floorsHeader.netRentableArea}`);
    const totalFloorsInit = aboveGroundFloors.length;
    if (!isNaN(nraInitVal) && totalFloorsInit > 0) {
      this.minFloorArea = Math.ceil(nraInitVal / totalFloorsInit);
      this.minFloorAreaUOM = this.floorsHeader.netRentableAreaUOM ?? this.floorsHeader.minimumFloorAreaUOM;
    } else {
      this.minFloorArea = this.floorsHeader.minimumFloorArea;
      this.minFloorAreaUOM = this.floorsHeader.minimumFloorAreaUOM;
    }
    this.maxFloorArea = this.floorsHeader.maximumFloorArea;
    this.maxFloorAreaUOM = this.floorsHeader.maximumFloorAreaUOM;

    const underGroundFloorsRaw = this.floorsHeader.underGroundFloors;
    const numberOfHiddenFloors = aboveGroundFloors.filter(
      item => item.hideFloor && item.floorIndex < maxExistingFloorIndex
    ).length;
    const numberOfHiddenParkingFloors = parkingFloors.filter(
      item => item.hideFloor && item.floorIndex > minExistingFloorIndex
    ).length;

    // index used to start floors from
    const startFloorsFromIndex = FloorClass.getStartingFloorIndex(this.localeId);
    const minimumAboveGroundValidation = maxExistingFloorIndex - numberOfHiddenFloors - startFloorsFromIndex;

    const generalFG = this.fb.group({
      propertyNRA: this.fb.group(
        {
          val: [this.floorsHeader.netRentableArea],
          uom: [this.floorsHeader.netRentableAreaUOM],
        },
        { validators: [FloorsValidators.notEmptyUOM, FloorsValidators.numericValuesOnly] }
      ),
      propertyCommonFloorSize: this.fb.group(
        {
          val: [this.floorsHeader.commonFloorSize],
          uom: [this.floorsHeader.commonFloorSizeUOM],
        },
        {
          validators: FloorsValidators.commonFloorSizeValidators(
            this.minFloorArea,
            this.minFloorAreaUOM,
            this.maxFloorArea,
            this.maxFloorAreaUOM,
            this.floorsHeader.commonFloorSize
          ),
        }
      ),
      numberOfFloors: new UntypedFormControl(this.floorsHeader.totalNumberOfFloors, [
        FloorsValidators.asWarning(
          Validators.min(minimumAboveGroundValidation),
          Validators.max(FloorClass.maxNumberOfFloorsAboveGround),
          Validators.required
        ),
        Validators.max(FloorClass.maxNumberOfFloorsAboveGround),
      ]),
      underGroundFloors: new UntypedFormControl(underGroundFloorsRaw != null ? Math.abs(underGroundFloorsRaw) : null, [
        FloorsValidators.asWarning(
          Validators.min(Math.abs(minExistingFloorIndex) - numberOfHiddenParkingFloors),
          Validators.max(FloorClass.maxNumberOfFloorsUnderGround),
          Validators.required
        ),
        Validators.max(FloorClass.maxNumberOfFloorsUnderGround),
      ]),
      // TODO: control to be used when checkbox option can get stored on DB
      setAvgFloorSize: new UntypedFormControl(false),
    });

    this.floorsFG = this.fb.group({
      general: generalFG, // From Group
      floor: this.generateFloorsFormArray(this.floors), // From Array
    });

    // subscribe to main inputs uom changes
    this.subscribeToPropertyMainUOM();
    this.subscribeToNraAndFloorsChanges();
  }

  private generateFloorsFormArray(floors: FloorClass[]): UntypedFormArray {
    const floorTableFGs: UntypedFormGroup[] = floors.map(floorsData => {
      const eachFormGroup = this.initFloorItemGroup(floorsData);
      this.formatNumericData(eachFormGroup);
      return eachFormGroup;
    });
    this.subscribeToFloorsFormGroups(floorTableFGs);
    return this.fb.array(floorTableFGs, {
      validators: [FloorsValidators.duplicatedFloorNumberValidator],
    });
  }

  private updateFloorValuesFromFormArray(floors: FloorClass[], floorFormArray: UntypedFormArray) {
    floorFormArray.controls
      .filter(floorFC => floorFC.touched)
      .forEach(floorFC => {
        const floorValue: FloorFGModel = floorFC.value;
        const index = floorValue.floorIndex;
        const floorToUpdate = floors.find(floor => floor.floorIndex === index);
        floorToUpdate.floorDesc = floorValue.floorName;
        floorToUpdate.floorArea = floorValue.floorSize ? floorValue.floorSize.val : null;
        floorToUpdate.floorAreaUOM = floorValue.floorSize ? floorValue.floorSize.uom : null;
        floorToUpdate.floorHeight = floorValue?.floorHeight?.val ? Number(floorValue.floorHeight.val) : null;
        floorToUpdate.floorHeightUOM = floorValue.floorHeight ? floorValue.floorHeight.uom : null;
        floorToUpdate.totalGrossArea = floorValue.floorGrossArea ? floorValue.floorGrossArea.val : null;
        floorToUpdate.totalGrossAreaUOM = floorValue.floorGrossArea ? floorValue.floorGrossArea.uom : null;
        floorToUpdate.floorNumber = isNotNullOrEmptyString(floorValue.floorNumber) ? floorValue.floorNumber : null;
        floorToUpdate.hideFloor = floorValue.hideFloor;
        floorToUpdate.floorId =
          floorToUpdate.floorId !== floorValue.floorId ? floorValue.floorId : floorToUpdate.floorId;
        floorToUpdate.floorImageURL = floorValue.floorPlan.link;
      });
  }

  /**
   * This is the common method to initialize the formArray group in
   * {@link formInit} and for new floors in {@link updateFloorsPositions}
   */
  private initFloorItemGroup(floorsData?: FloorClass) {
    return this.fb.group(
      {
        floorId: [floorsData && floorsData.floorId != null ? floorsData.floorId : null],
        floorIndex: [floorsData && floorsData.floorIndex != null ? floorsData.floorIndex : null],
        floorNumber: [
          isNotNullOrEmptyString(floorsData?.floorNumber) ||
          isNotNullOrEmptyString(floorsData?.floorId) ||
          floorsData?.spaces?.length > 0
            ? floorsData.floorNumber
            : '',
        ],
        floorName: [floorsData?.floorDesc],
        hideFloor: [!!floorsData?.hideFloor],
        floorSize: this.fb.group(
          {
            val: [floorsData?.floorArea],
            uom: [floorsData?.floorAreaUOM],
          },
          { validators: [FloorsValidators.notEmptyUOM] }
        ),
        floorGrossArea: this.fb.group(
          {
            val: [floorsData?.totalGrossArea],
            uom: [floorsData?.totalGrossAreaUOM],
          },
          { validators: [FloorsValidators.notEmptyUOM] }
        ),
        floorHeight: this.fb.group(
          {
            val: [floorsData?.floorHeight],
            uom: [floorsData?.floorHeightUOM],
          },
          { validators: [FloorsValidators.notEmptyUOM] }
        ),
        floorPlan: this.fb.group({
          link: [floorsData?.floorImageURL ?? null],
          name: [floorsData?.generateFloorPlanFileName() ?? null],
          id: [floorsData?.floorPlanId ?? null],
        }),
      },
      { validators: [FloorsValidators.floorNumberEmptyValidator] }
    );
  }

  private subscribeToNraAndFloorsChanges(): void {
    this.generalNraFG.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      const nraVal = parseFloat(this.generalNraFG.value?.val);
      const nraUom: string = this.generalNraFG.value?.uom;
      const tableFloorCount = FloorClass.getAboveGround(this.floors).length;

      if (!isNaN(nraVal) && tableFloorCount > 0) {
        this.minFloorArea = Math.ceil(nraVal / tableFloorCount);
        this.minFloorAreaUOM = nraUom;
      } else {
        this.minFloorArea = undefined;
        this.minFloorAreaUOM = undefined;
      }

      this.generalCommonFloorSizeFG.setValidators(
        FloorsValidators.commonFloorSizeValidators(
          this.minFloorArea,
          this.minFloorAreaUOM,
          this.maxFloorArea,
          this.maxFloorAreaUOM,
          this.generalCommonFloorSizeFG.value?.val
        )
      );
      this.generalCommonFloorSizeFG.updateValueAndValidity({ emitEvent: false });
    });
  }

  private subscribeToPropertyMainUOM(): void {
    this.subscribeToFloorSizesValAndUomLOV(this.generalNraFG);
    this.subscribeToFloorSizesValAndUomLOV(this.generalCommonFloorSizeFG);
    this.subscribeToFloorSizesUomValues(this.generalNraFG);
    this.subscribeToCommonFloorSizeChanges();
  }

  private subscribeToCommonFloorSizeChanges(): void {
    const valControl = this.generalCommonFloorSizeFG.controls.val as UntypedFormControl;
    const uomControl = this.generalCommonFloorSizeFG.controls.uom as UntypedFormControl;

    merge(valControl.valueChanges, uomControl.valueChanges)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.generalCommonFloorSizeFG.setValidators(
          FloorsValidators.commonFloorSizeValidators(
            this.minFloorArea,
            this.minFloorAreaUOM,
            this.maxFloorArea,
            this.maxFloorAreaUOM,
            this.generalCommonFloorSizeFG.value?.val
          )
        );
        this.generalCommonFloorSizeFG.updateValueAndValidity({ emitEvent: false });
      });
  }

  /**
   * Subscribes to UOM LOVs method request and the passed floor size form group
   * to, on any size change, update its UOM value to an existing one (nra or
   * common size) or the first uom value from the LOV.
   *
   * @param valueUomGroup
   */
  private subscribeToFloorSizesValAndUomLOV(valueUomGroup: UntypedFormGroup) {
    const nraUomValue: string = (this.generalNraFG.value.uom as string) || null;
    const commonSizeUomValue: string = (this.generalCommonFloorSizeFG.value.uom as string) || null;

    const valControl: UntypedFormControl = valueUomGroup.controls.val as UntypedFormControl;
    const uomControl: UntypedFormControl = valueUomGroup.controls.uom as UntypedFormControl;

    merge(valControl.valueChanges, this.uomsLoaded$)
      .pipe(
        untilDestroyed(this),
        startWith(valControl.value),
        filter(() => {
          return (
            isNotNullOrEmptyString(valControl.value) &&
            !isNotNullOrEmptyString(uomControl.value) &&
            this.uomValues != null &&
            this.uomValues.length > 0
          );
        })
      )
      .subscribe(() => {
        uomControl.setValue(nraUomValue || commonSizeUomValue || this.uomValues[0].key);
      });
  }

  /**
   * Get the general form group —the form group above the table— and subscribe
   * to the uom form control from the property NRA form group, applying UOM
   * value changes to all the existing floor size UOMs if the property NRA UOM
   * value gets updated.
   *
   * @see changeFloorsUOMValues
   */
  private subscribeToFloorSizesUomValues(valueUomGroup: UntypedFormGroup) {
    const uomGroup: UntypedFormControl = valueUomGroup.controls.uom as UntypedFormControl;
    uomGroup.valueChanges.pipe(untilDestroyed(this)).subscribe(this.changeFloorsUOMValues.bind(this));
  }

  private subscribeToFloorsFormGroups(floorsFG: UntypedFormGroup[]) {
    if (this.floorsFGSubscription != null) {
      this.floorsFGSubscription.unsubscribe();
    }
    const floorFGChangesArray = floorsFG.map(floorFormGroup =>
      floorFormGroup.valueChanges.pipe(
        map(() => floorFormGroup),
        debounceTime(300)
      )
    );
    this.floorsFGSubscription = merge(...floorFGChangesArray)
      .pipe(untilDestroyed(this))
      .subscribe(this.populateFloorFGValues.bind(this));
  }

  /**
   * Gets the floor size uom control for all the floors form groups and updates
   * its value only for the controls where values is not null or empty.
   */
  private changeFloorsUOMValues(propertyNraUOMValueChange: string) {
    const nraUomControl = this.generalNraFG.controls.uom as UntypedFormControl;
    const commonFloorSizeUomControl = this.generalCommonFloorSizeFG.controls.uom as UntypedFormControl;

    nraUomControl.setValue(propertyNraUOMValueChange, { emitEvent: false });
    commonFloorSizeUomControl.setValue(propertyNraUOMValueChange, { emitEvent: false });

    this.changeFloorsSizesFGValues('uom', propertyNraUOMValueChange);

    // Immediately update minFloorAreaUOM so the warning recalculates with the
    // new UOM instead of waiting for the generalNraFG.valueChanges propagation
    if (this.minFloorArea != null) {
      this.minFloorAreaUOM = propertyNraUOMValueChange;
      this.generalCommonFloorSizeFG.setValidators(
        FloorsValidators.commonFloorSizeValidators(
          this.minFloorArea,
          this.minFloorAreaUOM,
          this.maxFloorArea,
          this.maxFloorAreaUOM,
          this.generalCommonFloorSizeFG.value?.val
        )
      );
      this.generalCommonFloorSizeFG.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Auto-populate floors sizes or UOMs, using the provided value. Some
   * filtering and custom flows are required.
   * - If the required control to be updated is the floor (val), the method must
   *   update only floors that contain a floor number (existent floors)
   * - If the required control to be updated is the uom, the method must update
   *   only floors that contain a size value (val)
   *
   * @param control
   * @param value
   */
  private changeFloorsSizesFGValues(control: 'val' | 'uom', value: string | number | boolean) {
    const selectedFloorIndexes = this.selectedFloors.map(({ floorGroup }) => floorGroup.value.floorIndex);
    // Filters The selected floors only
    const filteredFloors: AbstractControl[] = this.floorFormArray.controls.filter((floorFG: UntypedFormGroup) => {
      const { floorIndex } = floorFG.controls;
      return selectedFloorIndexes.includes(floorIndex.value);
    });
    const filteredFloorsUpdate: {
      floorNumber: UntypedFormControl;
      hideFloor: UntypedFormControl;
      val: UntypedFormControl;
      uom: UntypedFormControl;
    }[] = filteredFloors.map((floorFG: UntypedFormGroup) => {
      const floorNumberFC: UntypedFormControl = floorFG.controls.floorNumber as UntypedFormControl;
      const floorSizeFG: UntypedFormGroup = floorFG.controls.floorSize as UntypedFormGroup;
      const hideFloorFC: UntypedFormControl = floorFG.controls.hideFloor as UntypedFormControl;
      return { floorNumber: floorNumberFC, hideFloor: hideFloorFC, ...floorSizeFG.controls } as {
        floorNumber: UntypedFormControl;
        hideFloor: UntypedFormControl;
        val: UntypedFormControl;
        uom: UntypedFormControl;
      };
    });
    filteredFloorsUpdate.forEach(valAndUom => {
      const fcToUpdate = valAndUom[control];
      fcToUpdate.setValue(value);
      fcToUpdate.markAsTouched();
      fcToUpdate.markAsDirty();
    });
  }

  /**
   * Updates floor form group values for different scenarios. One of the
   * scenarios is to fill the floor number —if empty— with the existing floor
   * index if any value of the floor gets updated.
   */
  private populateFloorFGValues(floorFormGroupChanges: UntypedFormGroup) {
    const floorChangedValue: FloorFGModel = floorFormGroupChanges.value;

    if (!isNotNullOrEmptyString(floorChangedValue.floorId)) {
      // Security: use uuidv4 to prevent predictable temporary floor IDs.
      const uuidHex = uuidV4().replace(/-/g, '').slice(0, 8);
      floorFormGroupChanges.controls.floorId.patchValue(((parseInt(uuidHex, 16) >>> 0) % 100000) * -1);
    }
    if (!isNotNullOrEmptyString(floorChangedValue.floorNumber)) {
      floorFormGroupChanges.controls.floorNumber.patchValue(floorChangedValue.floorIndex.toString());
    }
    this.#updateUOMValueIfNull(floorFormGroupChanges.controls.floorSize as UntypedFormGroup);
    this.#updateUOMValueIfNull(floorFormGroupChanges.controls.floorGrossArea as UntypedFormGroup);
    this.#updateDistanceUOMValueIfNull(floorFormGroupChanges.controls.floorHeight as UntypedFormGroup);
  }

  #updateUOMValueIfNull(formGroup) {
    const value = formGroup.value;
    if (value && isNotNullOrEmptyString(value.val) && !isNotNullOrEmptyString(value.uom)) {
      formGroup.controls.uom.setValue(this.generalNraFG.controls.uom.value);
    }
  }

  #updateDistanceUOMValueIfNull(formGroup) {
    const value = formGroup.value;
    if (value && isNotNullOrEmptyString(value.val) && !isNotNullOrEmptyString(value.uom)) {
      formGroup.controls.uom.setValue(UOM_VALUES.Feet);
    }
  }

  public formatNumericData(formGroup) {
    const floorheightCtrl = formGroup?.controls?.floorHeight as UntypedFormGroup;
    const val = floorheightCtrl.value.val != null ? Number(floorheightCtrl.value.val).toFixed(1) : null;
    floorheightCtrl.controls.val.patchValue(val);
  }

  /**
   * Request and assign UOM LOV
   */
  private getUomLOVs(): Observable<LovValues[][]> {
    return forkJoin([
      this.actionsToolbarService.getAreaLOVs().pipe(
        filter(Boolean),
        tap(areaLOV => {
          this.uomValues = areaLOV;
        })
      ),
      this.actionsToolbarService.getDistanceLOVs().pipe(
        filter(Boolean),
        tap(areaLOV => {
          this.distanceUOMValues = areaLOV;
        })
      ),
    ]).pipe(share());
  }

  /**
   * Initialize floor options for move-floor component
   */
  private initFloorOptions() {
    this.floorsOptions = FloorClass.generateFloorCatalogOptionsFrom(this.floors, {
      disableIfFloorNumberValue: false,
      useIndexAsValue: true,
      showIndex: this.toggleRouterService.isFeatureEnabled(SHOW_FLOOR_INDEX_FEATURE_KEY),
      showHiddenFloors: true,
      showInvalidFloors: false,
    });
  }
}
